import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "./Logo";
import { useLang } from "@/context/LanguageContext";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { id: "#about", key: "about" },
  { id: "#lifecycle", key: "lifecycle" },
  { id: "#brands", key: "brands" },
  { id: "#industries", key: "industries" },
  { id: "#contact", key: "contact" },
];

export default function Nav() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);

  const go = (e, id) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-ink/85 backdrop-blur-xl">
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-4 px-5 sm:px-8">
        <a href="#top" data-testid="nav-logo" onClick={(e) => go(e, "#top")}>
          <Logo />
        </a>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={l.id}
              data-testid={`nav-link-${l.key}`}
              onClick={(e) => go(e, l.id)}
              className="text-[13px] font-medium uppercase tracking-wide text-slate-300 transition-colors hover:text-white"
            >
              {t.nav[l.key]}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#contact"
            data-testid="nav-cta"
            onClick={(e) => go(e, "#contact")}
            className="hidden items-center gap-2 rounded-full bg-crimson px-5 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-crimson-hover md:inline-flex"
          >
            <i className="fa-solid fa-file-invoice" />
            {t.nav.cta}
          </a>

          <button
            className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white lg:hidden"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            <i className={`fa-solid ${open ? "fa-xmark" : "fa-bars"}`} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            data-testid="nav-mobile-menu"
            className="border-t border-white/10 bg-ink/95 px-5 py-6 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col">
              {LINKS.map((l) => (
                <a
                  key={l.id}
                  href={l.id}
                  data-testid={`nav-mobile-link-${l.key}`}
                  onClick={(e) => go(e, l.id)}
                  className="border-b border-white/5 py-3 font-display text-lg uppercase tracking-wide text-white"
                >
                  {t.nav[l.key]}
                </a>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <a
                href="#contact"
                data-testid="nav-mobile-cta"
                onClick={(e) => go(e, "#contact")}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-crimson px-5 py-2.5 text-[13px] font-semibold text-white"
              >
                <i className="fa-solid fa-file-invoice" />
                {t.nav.cta}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
